import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Plus, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldSelect } from '@/shared/components/custom/form-field/FormFieldSelect';
import { templateTaskSchema } from '../schemas';
import type { TemplateTaskFormValues } from '../schemas';

interface TemplateTaskPopoverFormProps {
  onAdd: (values: TemplateTaskFormValues) => void;
  isPending?: boolean;
}

export function TemplateTaskPopoverForm({
  onAdd,
  isPending = false,
}: TemplateTaskPopoverFormProps) {
  const { t } = useTranslation('projectTemplates');
  const { t: tTasks } = useTranslation('tasks');
  const [open, setOpen] = useState(false);

  const { control, handleSubmit, reset } = useForm<TemplateTaskFormValues>({
    resolver: zodResolver(templateTaskSchema),
    defaultValues: { title: '', priority: 'medium' },
  });

  const priorityOptions = (['urgent', 'high', 'medium', 'low'] as const).map((p) => ({
    value: p,
    label: tTasks(`priority.${p}`),
  }));

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) reset({ title: '', priority: 'medium' });
  }

  function handleFormSubmit(values: TemplateTaskFormValues) {
    onAdd(values);
    handleOpenChange(false);
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="justify-start gap-2 text-muted-foreground"
          data-testid="project-templates-task-add-button"
        >
          <Plus className="size-4" />
          {t('editor.tasks.addTaskLabel')}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-72 p-3"
        align="start"
        data-testid="project-templates-task-popover"
      >
        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-3">
          <FormFieldInput
            control={control}
            name="title"
            label={t('editor.tasks.titleLabel')}
            floatingLabel
            disabled={isPending}
            data-testid="project-templates-task-title-input"
          />

          <FormFieldSelect
            control={control}
            name="priority"
            label={t('editor.tasks.priorityLabel')}
            floatingLabel
            options={priorityOptions}
            disabled={isPending}
            data-testid="project-templates-task-priority-select"
          />

          <Button
            type="submit"
            size="sm"
            disabled={isPending}
            data-testid="project-templates-task-submit-button"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('actions.add')}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
